import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

// Self-contained editor for a content "page" (home, about, connect, directors, projects).
// groups: [{ title, fields: [{ k, label, area? }] }]
export const PageContentEditor = ({ page, groups, prefix, heading, description }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/content/${page}`);
      setData(res.data);
    } catch (e) {
      toast.error("Could not load content.");
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put(`/admin/content/${page}`, data);
      setData(saved);
      toast.success("Saved. Refresh the page to see changes.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const SaveBtn = ({ testid }) => (
    <button
      data-testid={testid}
      disabled={saving || !data}
      onClick={save}
      className="inline-flex items-center gap-2 rounded-full bg-sp-navy px-6 py-2.5 text-sm text-white hover:bg-sp-deep transition-colors duration-300 disabled:opacity-60"
    >
      {saving ? "Saving…" : "Save Changes"}
    </button>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-sp-deep">{heading}</h2>
          {description && <p className="font-sans text-sm text-sp-soft mt-1 max-w-2xl">{description}</p>}
        </div>
        <SaveBtn testid={`${prefix}-save-btn`} />
      </div>

      {!data ? (
        <p className="text-sp-soft font-sans">Loading content…</p>
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <div key={group.title} className="bg-white border border-sp-navy/10 rounded-sm p-6">
              <h3 className="font-serif text-lg text-sp-deep mb-5">{group.title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {group.fields.map((f) => (
                  <div key={f.k} className={f.area ? "md:col-span-2" : ""}>
                    <Label className="text-sp-deep text-xs">{f.label}</Label>
                    {f.area ? (
                      <Textarea
                        data-testid={`${prefix}-field-${f.k}`}
                        className="mt-1.5"
                        rows={3}
                        value={data[f.k] || ""}
                        onChange={(e) => setData((s) => ({ ...s, [f.k]: e.target.value }))}
                      />
                    ) : (
                      <Input
                        data-testid={`${prefix}-field-${f.k}`}
                        className="mt-1.5"
                        value={data[f.k] || ""}
                        onChange={(e) => setData((s) => ({ ...s, [f.k]: e.target.value }))}
                      />
                    )}
                    {f.k.toLowerCase().includes("image") && data[f.k] ? (
                      <img src={data[f.k]} alt="" className="mt-2 h-16 w-28 object-cover rounded-sm border border-sp-navy/10" />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="flex justify-end">
            <SaveBtn testid={`${prefix}-save-btn-bottom`} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PageContentEditor;
